import React from 'react';
import OffersToolbar from './OffersToolbar';
import OffersEmpty from './OffersEmpty';
import OffersError from './OffersError';
import OffersLoading from './OffersLoading';
import OfferCard from '../card/OfferCard';
import CompetingOffersGroup from './CompetingOffersGroup';
import { partitionCompetingOffers } from './competingOffers';
import './OffersList.css';

/**
 * OffersList — toolbar + state-aware container that renders OfferCard items.
 * Parent passes already-filtered+sorted `offers` plus `rawOffersForCounts`
 * for chip counts.
 */
export default function OffersList({
  offers = [],
  loading = false,
  error = null,
  currentRole = 'buyer',
  toolbarState,
  onToolbarChange,
  onRefresh,
  onAction,
  onViewHouse,
  onViewHistory,
  onCreate,
  onClearFilters,
  rawOffersForCounts = [],
}) {
  const hasFilters =
    Boolean(toolbarState?.search) ||
    (toolbarState?.statusFilter && toolbarState.statusFilter !== 'all');

  let body;
  if (error) {
    body = <OffersError error={error} onRetry={onRefresh} />;
  } else if (loading && offers.length === 0) {
    body = <OffersLoading count={4} />;
  } else if (!loading && offers.length === 0) {
    body = (
      <OffersEmpty
        role={currentRole}
        hasFilters={hasFilters}
        onClearFilters={onClearFilters}
        onCreate={onCreate}
      />
    );
  } else if (currentRole === 'seller') {
    // Sellers get a flat list across every listing; group it by property so the
    // offers competing on the same home sit together, with the lone-offer
    // listings in the usual grid below.
    const { competing, singles } = partitionCompetingOffers(offers, rawOffersForCounts);
    body = (
      <div className="offers-list-groups">
        {competing.map((group) => (
          <CompetingOffersGroup
            key={group.key}
            group={group}
            currentRole={currentRole}
            onAction={onAction}
            onViewHouse={onViewHouse}
            onViewHistory={onViewHistory}
          />
        ))}
        {singles.length > 0 ? (
          <ul className="offers-list">
            {singles.map((offer, i) => (
              <li key={offer.id} className="offers-list__item">
                <OfferCard
                  offer={offer}
                  currentRole={currentRole}
                  onAction={onAction}
                  onViewHouse={onViewHouse}
                  onViewHistory={onViewHistory}
                  index={i}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  } else {
    body = (
      <ul className="offers-list">
        {offers.map((offer, i) => (
          <li key={offer.id} className="offers-list__item">
            <OfferCard
              offer={offer}
              currentRole={currentRole}
              onAction={onAction}
              onViewHouse={onViewHouse}
              onViewHistory={onViewHistory}
              index={i}
            />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="offers-list-root">
      <OffersToolbar
        offers={rawOffersForCounts}
        state={toolbarState}
        onChange={onToolbarChange}
        onRefresh={onRefresh}
        loading={loading}
      />
      {body}
    </div>
  );
}
